/**
 * mul-div-exam — 乘除考卷(第 7 單元 乘法與除法)線上互動卷 + 純計分函式。
 *
 * 對應 time-exam.ts / stats-table-exam.ts / math-exam.ts 同一套形狀:分大題作答 → 交卷
 * 即時計分 + 逐題對錯 + 正解 + 錯題回流。純計分 `scoreMulDivExam` / 答案正規化
 * `normalizeMulDivAnswer` 抽出可單測(無 DOM 依賴)。
 *
 * 乘除互逆「一組四算式」題答 3 行算式 → 用 textarea + 多行寬鬆比對(順序不拘、去空白)。
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕。
 * 專業介面鐵則:無音效、無 mascot、無彩光、無戲謔文案。
 *
 * 學習/遊戲分離:純學習側,只動學習數據,**絕不**碰 commanderXp / 戰鬥數值。
 */

import type { MulDivExam, MulDivSection, MulDivQuestion } from './mul-div-gen';
import { pageShell, academicButton } from '../ui/components';

// ───────────────────────────── 答案正規化 + 比對 ─────────────────────────────

/**
 * 正規化一個乘除答案字串以利寬鬆比對:
 *   - 全形數字 → 半形;全形 × ÷ = 與中文「乘除等於」相關符號 → 半形運算子。
 *   - 去所有空白。
 *   - 小寫化(英數;mL 等單位大小寫不敏感)。
 * 例:'20' / '２０' → '20';'4×7=28' / '4ｘ7＝28' → '4*7=28';'13 片' / '13片' → '13片'。
 */
export function normalizeMulDivAnswer(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();
  // 全形數字 → 半形。
  s = s.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xff10 + 0x30));
  // 全形英文字母 → 半形(供 mL 等單位)。
  s = s.replace(/[Ａ-Ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff21 + 0x41));
  s = s.replace(/[ａ-ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff41 + 0x61));
  // 乘除符號統一(全形 × ÷ 與字母 x、星號 → 統一記號;= 全形 → 半形)。
  s = s.replace(/[×✕✖xX＊*]/g, '*');
  s = s.replace(/[÷]/g, '/');
  s = s.replace(/[＝]/g, '=');
  // 去所有空白 + 小寫。
  s = s.replace(/\s+/g, '').toLowerCase();
  return s;
}

/** 把多行答案拆成正規化後的算式集合(順序不拘,供互逆題比對)。 */
function normalizeLineSet(raw: string): string[] {
  return raw
    .split(/[\n,、;；]+/u)
    .map((line) => normalizeMulDivAnswer(line))
    .filter((line) => line !== '')
    .sort();
}

/**
 * 一格是否答對(寬鬆比對):
 *   1. 正規化後完全相符 → 對。
 *   2. 多行算式題(正解含換行):拆行排序後集合相等 → 對(順序不拘)。
 *   3. 去尾綴單位後相符(數值題「20」==「20 片」;但「商 11 餘 5」這類含中文的維持整段比)。
 */
export function isMulDivAnswerCorrect(studentRaw: string | undefined, correct: string): boolean {
  if (studentRaw === undefined) return false;
  const c = correct;
  // 多行算式題(正解含換行):集合比對(順序不拘)。
  if (c.includes('\n')) {
    const want = normalizeLineSet(c);
    const got = normalizeLineSet(studentRaw);
    return want.length > 0 && want.length === got.length && want.every((v, i) => v === got[i]);
  }
  const s = normalizeMulDivAnswer(studentRaw);
  const cn = normalizeMulDivAnswer(c);
  if (s === '') return false;
  if (s === cn) return true;
  // 數值寬鬆:去尾綴單位(非數字尾段)後比。僅當正解是「數字+單位」形(無餘 / 中文判讀詞)。
  if (/^\d+[^\d]*$/.test(cn) && !cn.includes('餘') && !cn.includes('商')) {
    const stripUnit = (x: string): string => x.replace(/[^\d].*$/u, '');
    if (stripUnit(s) !== '' && stripUnit(s) === stripUnit(cn)) return true;
  }
  return false;
}

// ───────────────────────────── 純計分 ─────────────────────────────

/** 學生整卷作答:`"sectionIdx:questionIdx" → 字串`。 */
export type MulDivExamAnswers = Record<string, string>;

/** 單題判定結果。 */
export interface MulDivItemResult {
  itemId: string;
  correct: boolean;
  /** 正解顯示字串。 */
  correctLabel: string;
  /** 學生作答顯示字串(未作答 = 空)。 */
  chosenLabel: string;
  points: number;
  earned: number;
}

/** 整卷計分結果。 */
export interface MulDivExamScore {
  score: number;
  total: number;
  correctCount: number;
  itemCount: number;
  results: MulDivItemResult[];
}

/**
 * 純計分:逐題批改(每題一格)。可單測(無 DOM 依賴)。
 *
 * @param exam    一份乘除考卷。
 * @param answers 整卷作答(`"sectionIdx:questionIdx" → 字串`)。
 * @returns 得分 / 滿分 / 逐題結果。
 */
export function scoreMulDivExam(exam: MulDivExam, answers: MulDivExamAnswers): MulDivExamScore {
  const results: MulDivItemResult[] = [];
  let score = 0;
  let total = 0;
  let correctCount = 0;
  let itemCount = 0;

  exam.sections.forEach((section, sIdx) => {
    section.questions.forEach((q, qIdx) => {
      itemCount += 1;
      total += section.pointsEach;
      const studentRaw = answers[`${sIdx}:${qIdx}`];
      const ok = isMulDivAnswerCorrect(studentRaw, q.answer);
      const earned = ok ? section.pointsEach : 0;
      if (ok) {
        correctCount += 1;
        score += section.pointsEach;
      }
      results.push({
        itemId: q.id,
        correct: ok,
        correctLabel: q.answer,
        chosenLabel: studentRaw && studentRaw.trim() !== '' ? studentRaw.trim() : '',
        points: section.pointsEach,
        earned,
      });
    });
  });

  return { score, total, correctCount, itemCount, results };
}

// ───────────────────────────── 互動卷 UI ─────────────────────────────

/** 交卷結果回呼:錯題(供回流錯題本)。 */
export interface MulDivExamWrong {
  question: MulDivQuestion;
  sectionKind: MulDivSection['kind'];
}

/**
 * Render 線上乘除考卷 into `container`。每大題列題 + 填答格 → 「交卷」即時計分 + 逐題對錯。
 *
 * @param container 掛載點。
 * @param exam      一份(產生)乘除考卷。
 * @param onDone    離開 / 完成回呼。
 * @param onSubmit  選填:交卷後回呼(帶分數 + 錯題),供錯題回流。
 */
export function runMulDivExam(
  container: HTMLElement,
  exam: MulDivExam,
  onDone: () => void,
  onSubmit?: (score: MulDivExamScore, wrong: MulDivExamWrong[]) => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: `${exam.scope} · 在每格寫出答案,作答後按「交卷」看分數與正解。`,
    onBack: onDone,
    confirmOnBack: '離開後這張卷的作答不會保留,確定?',
  });

  const root = document.createElement('div');
  root.className = 'mul-div-exam';
  root.setAttribute('data-testid', 'mul-div-exam');
  root.style.cssText = 'display:flex;flex-direction:column;gap:22px;';
  shell.body.appendChild(root);

  const answers: MulDivExamAnswers = {};
  let submitted = false;

  exam.sections.forEach((section, sIdx) => {
    const sectionEl = document.createElement('section');
    sectionEl.className = `md-section md-section--${section.kind}`;
    sectionEl.setAttribute('data-testid', `md-section-${section.kind}`);
    sectionEl.style.cssText = 'display:flex;flex-direction:column;gap:14px;';

    const head = document.createElement('div');
    head.className = 'md-section-head';
    head.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:18px;color:var(--ef-ink);' +
      'border-left:4px solid var(--ef-accent,#2f6b4f);padding-left:10px;';
    head.textContent = `${section.label}、${section.title}`;
    sectionEl.appendChild(head);

    const instr = document.createElement('div');
    instr.className = 'md-section-instr';
    instr.style.cssText = 'font-size:14px;color:var(--ef-sub);line-height:1.6;';
    instr.textContent = section.instructions;
    sectionEl.appendChild(instr);

    // 列題 + 填答格。
    section.questions.forEach((q, qIdx) => {
      sectionEl.appendChild(renderQuestion(sIdx, qIdx, q, answers, section.kind));
    });

    root.appendChild(sectionEl);
  });

  // ── 交卷 + 結果區 ──
  const actions = document.createElement('div');
  actions.className = 'md-actions';
  actions.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;margin-top:8px;';
  root.appendChild(actions);

  const resultEl = document.createElement('div');
  resultEl.className = 'md-result';
  resultEl.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  root.appendChild(resultEl);

  const submitBtn = academicButton({
    label: '交卷',
    variant: 'primary',
    onClick: () => {
      if (submitted) return;
      submitted = true;
      const result = scoreMulDivExam(exam, answers);
      renderResult(resultEl, result);
      submitBtn.disabled = true;
      submitBtn.classList.add('md-submit--done');
      resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (onSubmit) {
        const wrong: MulDivExamWrong[] = [];
        let flat = 0;
        exam.sections.forEach((section) => {
          section.questions.forEach((q) => {
            const r = result.results[flat];
            flat += 1;
            if (r && !r.correct) wrong.push({ question: q, sectionKind: section.kind });
          });
        });
        onSubmit(result, wrong);
      }
    },
  });
  submitBtn.classList.add('md-submit');
  submitBtn.setAttribute('data-testid', 'md-submit');
  actions.appendChild(submitBtn);

  const doneBtn = academicButton({
    label: '完成',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      onDone();
    },
  });
  doneBtn.classList.add('md-done');
  actions.appendChild(doneBtn);
}

/** 一題的容器(題號 + 題幹 + 一格輸入;互逆題用多行 textarea)。 */
function renderQuestion(
  sIdx: number,
  qIdx: number,
  q: MulDivQuestion,
  answers: MulDivExamAnswers,
  kind: MulDivSection['kind'],
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'md-q';
  row.dataset.itemId = q.id;
  row.style.cssText =
    'display:flex;flex-direction:column;gap:8px;padding:12px 14px;' +
    'background:var(--ef-paper-bright,#fff);border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:var(--ef-radius,8px);';

  const top = document.createElement('div');
  top.style.cssText = 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;';
  const num = document.createElement('span');
  num.style.cssText = 'font-weight:700;min-width:1.6em;color:var(--ef-ink);';
  num.textContent = `${qIdx + 1}.`;
  top.appendChild(num);
  const prompt = document.createElement('span');
  prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);line-height:1.6;white-space:pre-wrap;';
  prompt.textContent = q.prompt;
  top.appendChild(prompt);
  row.appendChild(top);

  const field = document.createElement('label');
  field.style.cssText = 'display:flex;align-items:flex-start;gap:6px;font-size:14px;color:var(--ef-sub);';
  const tag = document.createElement('span');
  tag.textContent = '答:';
  field.appendChild(tag);

  answers[`${sIdx}:${qIdx}`] = '';
  if (kind === 'inverse') {
    // 互逆題答 3 行算式 → 多行 textarea。
    const ta = document.createElement('textarea');
    ta.className = `md-input md-input--${q.id}`;
    ta.rows = 3;
    ta.autocomplete = 'off';
    ta.placeholder = '每行寫一個算式';
    ta.style.cssText =
      'min-width:12em;width:100%;max-width:18em;padding:6px 8px;font-size:15px;' +
      'border:1px solid var(--ef-line,#d8d2c4);border-radius:6px;' +
      'background:var(--ef-paper-bright,#fff);color:var(--ef-ink);resize:vertical;';
    ta.addEventListener('input', () => {
      answers[`${sIdx}:${qIdx}`] = ta.value;
    });
    field.appendChild(ta);
  } else {
    const input = document.createElement('input');
    input.type = 'text';
    input.className = `md-input md-input--${q.id}`;
    input.autocomplete = 'off';
    input.style.cssText =
      'min-width:8em;padding:6px 8px;font-size:15px;border:1px solid var(--ef-line,#d8d2c4);' +
      'border-radius:6px;background:var(--ef-paper-bright,#fff);color:var(--ef-ink);';
    input.addEventListener('input', () => {
      answers[`${sIdx}:${qIdx}`] = input.value;
    });
    field.appendChild(input);
  }
  row.appendChild(field);

  return row;
}

/** 結果區:總分 + 逐題對錯 + 正解。 */
function renderResult(el: HTMLElement, result: MulDivExamScore): void {
  el.innerHTML = '';
  el.style.display = 'flex';

  const headline = document.createElement('div');
  headline.className = 'md-score';
  headline.setAttribute('data-testid', 'md-score');
  headline.style.cssText =
    'font-family:var(--ef-serif);font-weight:700;font-size:24px;color:var(--ef-ink);' +
    'border-top:2px solid var(--ef-line,#d8d2c4);padding-top:14px;';
  headline.textContent = `分數:${result.score} / ${result.total}(答對 ${result.correctCount} / ${result.itemCount} 題)`;
  el.appendChild(headline);

  const list = document.createElement('div');
  list.className = 'md-result-list';
  list.style.cssText = 'display:flex;flex-direction:column;gap:8px;font-size:14px;';
  result.results.forEach((r, idx) => {
    const line = document.createElement('div');
    line.className = `md-result-line ${r.correct ? 'md-correct' : 'md-wrong'}`;
    const mark = r.correct ? '✓' : '✗';
    const chosen = r.chosenLabel || '(未作答)';
    // 多行正解 / 作答把換行換成「、」以單行顯示。
    const oneLine = (s: string): string => s.replace(/\n+/g, '、');
    line.style.color = r.correct ? 'var(--ef-ink)' : '#b03030';
    line.style.whiteSpace = 'pre-wrap';
    line.textContent = r.correct
      ? `${mark} 第 ${idx + 1} 題:${oneLine(r.correctLabel)}`
      : `${mark} 第 ${idx + 1} 題:你寫「${oneLine(chosen)}」→ 正解「${oneLine(r.correctLabel)}」`;
    list.appendChild(line);
  });
  el.appendChild(list);
}
